import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, logo } from "@/data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
          scrolled ? "bg-cream/85 backdrop-blur-md shadow-[0_1px_0_rgba(28,28,30,0.08)]" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={logo} alt="FreeRange.Education" className="h-11 w-11 rounded-full" />
            <span className="font-display text-lg font-bold tracking-tight text-charcoal">
              FreeRange<span className="text-turquoise">.Education</span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-link`}
                className={({ isActive }) =>
                  `relative rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-purple" : "text-charcoal/70 hover:text-charcoal"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:block">
            <Link
              to="/apps"
              data-testid="nav-cta"
              className="group inline-flex items-center gap-1.5 rounded-xl bg-purple px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-purple-dark"
            >
              Explore our apps
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            className="rounded-lg p-2 text-charcoal lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-7 w-7" />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-purple text-cream lg:hidden"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-3">
              <Link to="/" className="flex items-center gap-3" onClick={() => setOpen(false)}>
                <img src={logo} alt="" className="h-11 w-11 rounded-full" />
                <span className="font-display text-lg font-bold">FreeRange.Education</span>
              </Link>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2">
                <X className="h-7 w-7" />
              </button>
            </div>
            <div className="flex flex-col px-5 pt-8">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05 }}
                >
                  <NavLink
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-link`}
                    className="block border-b border-cream/15 py-4 font-display text-3xl font-bold tracking-tight"
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
